import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Eye, EyeOff, Trash2, Star, MapPin, Clock, Users } from 'lucide-react';

const MOCK_STAYS = [
  { id: 1, title: 'Séjour Riad Authentique', city: 'marrakech', category: 'Hébergement', duration: '7 jours', price: '1 200 EUR', active: true, rating: 4.8, spots: 4 },
  { id: 2, title: 'Cure Thermale & Bien-être', city: 'agadir', category: 'Bien-être', duration: '5 jours', price: '900 EUR', active: true, rating: 4.6, spots: 6 },
  { id: 3, title: 'Séjour Médical Palmeraie', city: 'marrakech', category: 'Médical', duration: '10 jours', price: '1 800 EUR', active: true, rating: 4.7, spots: 2 },
  { id: 4, title: 'Villa Senior Agadir', city: 'agadir', category: 'Résidence', duration: '14 jours', price: '2 200 EUR', active: false, rating: 4.5, spots: 3 },
  { id: 5, title: 'Week-end Ressourcement', city: 'marrakech', category: 'Bien-être', duration: '3 jours', price: '450 EUR', active: true, rating: 4.9, spots: 8 },
  { id: 6, title: 'Thalassothérapie Côtière', city: 'agadir', category: 'Bien-être', duration: '7 jours', price: '1 100 EUR', active: true, rating: 4.4, spots: 5 },
  { id: 7, title: 'Séjour Répit Famille', city: 'marrakech', category: 'Famille', duration: '5 jours', price: '800 EUR', active: false, rating: 4.3, spots: 4 },
  { id: 8, title: 'Résidence Atlas Vue', city: 'agadir', category: 'Résidence', duration: '21 jours', price: '3 500 EUR', active: true, rating: 4.6, spots: 2 },
];

const CATEGORIES = ['Hébergement', 'Bien-être', 'Médical', 'Résidence', 'Famille'];
const CITY_COLORS = { marrakech: 'bg-orange-100 text-orange-700', agadir: 'bg-brown-100 text-[#5A3B31] bg-[#F0EBE6]' };

const EMPTY = { title: '', city: 'marrakech', category: 'Hébergement', duration: '', price: '', spots: '', active: true };

export default function CatalogModule() {
  const [stays, setStays] = useState(MOCK_STAYS);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const filtered = stays.filter(s => filter === 'all' || s.city === filter || (filter === 'active' && s.active) || (filter === 'inactive' && !s.active));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...s }); setOpen(true); };

  const save = () => {
    if (!form.title.trim()) return toast.error('Titre requis');
    if (editing) {
      setStays(stays.map(s => s.id === editing ? { ...s, ...form } : s));
      toast.success('Séjour mis à jour');
    } else {
      setStays([...stays, { ...form, id: Date.now(), rating: 0 }]);
      toast.success('Séjour ajouté');
    }
    setOpen(false);
  };

  const toggleActive = (id) => {
    setStays(stays.map(s => s.id === id ? { ...s, active: !s.active } : s));
    toast.success('Statut mis à jour');
  };

  const remove = (id) => {
    const stay = stays.find(s => s.id === id);
    if (!window.confirm(`Supprimer « ${stay.title} » ?`)) return;
    setStays(stays.filter(s => s.id !== id));
    toast.success('Séjour supprimé');
  };

  return (
    <div className="p-6 space-y-6" data-testid="catalog-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-primary">Catalogue séjours</h2>
          <p className="text-sm text-muted-foreground font-body">{stays.filter(s => s.active).length} séjours publiés · {stays.length} au total</p>
        </div>
        <Button onClick={openAdd} className="rounded-full bg-accent hover:bg-accent/90 text-white gap-2 font-body" data-testid="catalog-add-btn">
          <Plus className="w-4 h-4" /> Ajouter un séjour
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        {[['all','Tous',stays.length], ['marrakech','Marrakech', stays.filter(s=>s.city==='marrakech').length], ['agadir','Agadir', stays.filter(s=>s.city==='agadir').length], ['active','Publiés', stays.filter(s=>s.active).length], ['inactive','Masqués', stays.filter(s=>!s.active).length]].map(([k,l,c]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-body font-medium transition-all ${filter===k ? 'bg-primary text-white' : 'bg-muted text-foreground/70 hover:bg-muted/80'}`}>
            {l} ({c})
          </button>
        ))}
      </div>

      {/* Catalogue — cartes */}
      <div className="space-y-3">
        {filtered.map(s => (
          <div key={s.id} className="bg-white rounded-2xl border border-border shadow-sm p-5 hover:border-primary/20 transition-all" data-testid={`catalog-item-${s.id}`}>
            <div className="flex items-start gap-4">
              {/* Ville badge */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.city === 'marrakech' ? 'bg-[#FFF3E8]' : 'bg-[#F0EBE6]'}`}>
                <MapPin className={`w-4 h-4 ${s.city === 'marrakech' ? 'text-[#F47B20]' : 'text-[#5A3B31]'}`} />
              </div>

              {/* Infos principales */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-heading font-semibold text-primary text-sm">{s.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.city === 'marrakech' ? 'bg-[#FFF3E8] text-[#F47B20]' : 'bg-[#F0EBE6] text-[#5A3B31]'}`}>
                    {s.city === 'marrakech' ? 'Marrakech' : 'Agadir'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-body">{s.category}</span>
                  <Badge className={s.active ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                    {s.active ? 'Publié' : 'Masqué'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-body">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration}</span>
                  <span className="flex items-center gap-1 font-semibold text-accent">{s.price}</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.spots} places</span>
                  {s.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#F47B20] text-[#F47B20]" />{s.rating}/5</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openEdit(s)} className="p-2 hover:bg-muted rounded-xl transition-colors" title="Modifier" data-testid={`catalog-edit-${s.id}`}>
                  <Edit className="w-4 h-4 text-muted-foreground" />
                </button>
                <button onClick={() => toggleActive(s.id)} className="p-2 hover:bg-muted rounded-xl transition-colors" title={s.active ? 'Masquer' : 'Publier'} data-testid={`catalog-toggle-${s.id}`}>
                  {s.active ? <EyeOff className="w-4 h-4 text-orange-500" /> : <Eye className="w-4 h-4 text-green-500" />}
                </button>
                <button onClick={() => remove(s.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors" title="Supprimer" data-testid={`catalog-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground font-body text-sm">Aucun séjour trouvé</div>
        )}
      </div>

      {/* Modal Add/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Modifier le séjour' : 'Nouveau séjour'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Titre *</label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Ex: Séjour Riad Authentique" className="font-body" data-testid="catalog-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Ville</label>
                <select value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-body">
                  <option value="marrakech">Marrakech</option>
                  <option value="agadir">Agadir</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Catégorie</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-body">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Durée</label>
                <Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} placeholder="7 jours" className="font-body" />
              </div>
              <div>
                <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Prix</label>
                <Input value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="1 200 EUR" className="font-body" />
              </div>
              <div>
                <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Places</label>
                <Input type="number" value={form.spots} onChange={e => setForm({...form, spots: e.target.value})} placeholder="4" className="font-body" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="active" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="rounded" />
              <label htmlFor="active" className="text-sm font-body text-foreground">Publier immédiatement</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="font-body rounded-full">Annuler</Button>
            <Button onClick={save} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full" data-testid="catalog-form-save">
              {editing ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
